import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getSuperAdminHeader } from './SuperAdminLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { CreditCard, Pencil, Loader2, DollarSign } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_MAP = {
  active: { label: 'Ativo', color: 'bg-green-500 text-white' },
  pending: { label: 'Pendente', color: 'bg-amber-500 text-white' },
  overdue: { label: 'Vencido', color: 'bg-red-500 text-white' },
  cancelled: { label: 'Cancelado', color: 'bg-gray-400 text-white' },
};

export function SuperAdminBillingPage() {
  const [billing, setBilling] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editTenant, setEditTenant] = useState(null);
  const [form, setForm] = useState({ monthly_price: 0, status: 'active', next_billing: '', payment_method: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/super/billing`, { headers: getSuperAdminHeader() });
      setBilling(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const openEdit = (t) => {
    setEditTenant(t);
    setForm({
      monthly_price: t.monthly_price || 0,
      status: t.status || 'active',
      next_billing: t.next_billing || '',
      payment_method: t.payment_method || '',
      notes: t.notes || '',
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/super/billing/${editTenant.tenant_id}`, form, { headers: getSuperAdminHeader() });
      toast.success('Faturamento atualizado');
      setEditTenant(null);
      fetchData();
    } catch (err) { toast.error('Erro ao atualizar'); }
    finally { setSaving(false); }
  };

  const totalMRR = billing.reduce((sum, b) => sum + (b.monthly_price || 0), 0);
  const activeCount = billing.filter(b => b.status === 'active').length;

  if (loading) return <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-12 w-full" />)}</div>;

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold">Faturamento</h2>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-white border-gray-200">
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">MRR (Receita Mensal)</p>
            <p className="text-3xl font-bold text-green-600">R$ {totalMRR.toLocaleString('pt-BR')}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-gray-200">
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Clientes Ativos</p>
            <p className="text-3xl font-bold">{activeCount}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-gray-200">
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Total Empresas</p>
            <p className="text-3xl font-bold">{billing.length}</p>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-white border-gray-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Empresa</TableHead>
                <TableHead className="w-24">Plano</TableHead>
                <TableHead className="w-32 text-right">Valor Mensal</TableHead>
                <TableHead className="w-28">Status</TableHead>
                <TableHead className="w-36">Próx. Cobrança</TableHead>
                <TableHead className="w-36">Forma Pgto</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {billing.map(b => {
                const st = STATUS_MAP[b.status] || STATUS_MAP.active;
                return (
                  <TableRow key={b.tenant_id}>
                    <TableCell className="font-medium">{b.company_name}</TableCell>
                    <TableCell><Badge variant="outline" className="text-xs capitalize">{b.plan}</Badge></TableCell>
                    <TableCell className="text-right font-semibold">R$ {(b.monthly_price || 0).toLocaleString('pt-BR')}</TableCell>
                    <TableCell><Badge className={`text-xs ${st.color}`}>{st.label}</Badge></TableCell>
                    <TableCell className="text-sm text-gray-500">{b.next_billing || '-'}</TableCell>
                    <TableCell className="text-sm text-gray-500">{b.payment_method || '-'}</TableCell>
                    <TableCell><Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(b)}><Pencil className="h-3.5 w-3.5" /></Button></TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={!!editTenant} onOpenChange={(v) => { if (!v) setEditTenant(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><CreditCard className="h-5 w-5" />{editTenant?.company_name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Valor Mensal (R$)</Label>
                <Input type="number" value={form.monthly_price} onChange={(e) => setForm({ ...form, monthly_price: parseFloat(e.target.value) || 0 })} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Ativo</SelectItem>
                    <SelectItem value="pending">Pendente</SelectItem>
                    <SelectItem value="overdue">Vencido</SelectItem>
                    <SelectItem value="cancelled">Cancelado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Próxima Cobrança</Label>
                <Input type="date" value={form.next_billing} onChange={(e) => setForm({ ...form, next_billing: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Forma de Pagamento</Label>
                <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                  <SelectTrigger><SelectValue placeholder="Selecionar" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="boleto">Boleto</SelectItem>
                    <SelectItem value="pix">PIX</SelectItem>
                    <SelectItem value="cartao">Cartão</SelectItem>
                    <SelectItem value="transferencia">Transferência</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Observações</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notas sobre faturamento..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTenant(null)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving}>{saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
